import  { useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import {
  AssignmentTurnedIn as TaskIcon,
  People as ClientIcon,
  AttachMoney as RevenueIcon,
  Work as WorkloadIcon,
  TrendingUp as GrowthIcon,
  Assessment as ReportIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompleteIcon,
} from "@mui/icons-material";
import Chart from "chart.js/auto";
import Layout from "../components/layout/Calayout";

const Cadashboard = () => {
  const theme = useTheme();
  const clientGrowthChartRef = useRef(null);
  const revenueChartRef = useRef(null);
  const taskCompletionChartRef = useRef(null);
  const clientChartInstanceRef = useRef(null);
  const revenueChartInstanceRef = useRef(null);
  const taskChartInstanceRef = useRef(null);

  useEffect(() => {
   
    if (clientChartInstanceRef.current) {
      clientChartInstanceRef.current.destroy();
    }
    if (revenueChartInstanceRef.current) {
      revenueChartInstanceRef.current.destroy();
    }
    if (taskChartInstanceRef.current) {
      taskChartInstanceRef.current.destroy();
    }

   
    const clientCtx = clientGrowthChartRef.current.getContext("2d");
    clientChartInstanceRef.current = new Chart(clientCtx, {
      type: "line",
      data: {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
        datasets: [
          {
            label: "Client Growth",
            data: [65, 59, 80, 81, 56, 85],
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            tension: 0.4,
            borderWidth: 3,
            pointBackgroundColor: theme.palette.primary.main,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
            pointRadius: 6,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: alpha(theme.palette.text.secondary, 0.1),
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
            },
          },
        },
      },
    });

    // Revenue Chart
    const revenueCtx = revenueChartRef.current.getContext("2d");
    revenueChartInstanceRef.current = new Chart(revenueCtx, {
      type: "bar",
      data: {
        labels: ["Q1", "Q2", "Q3", "Q4"],
        datasets: [
          {
            label: "Quarterly Revenue",
            data: [120000, 150000, 180000, 200000],
            backgroundColor: theme.palette.primary.main,
            borderRadius: 8,
            borderSkipped: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: alpha(theme.palette.text.secondary, 0.1),
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
              callback: function(value) {
                return '$' + (value / 1000) + 'K';
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: theme.palette.text.secondary,
              font: {
                family: theme.typography.fontFamily,
              },
            },
          },
        },
      },
    });

    // Task Completion Chart
    const taskCtx = taskCompletionChartRef.current.getContext("2d");
    taskChartInstanceRef.current = new Chart(taskCtx, {
      type: "doughnut",
      data: {
        labels: ["Completed", "In Progress", "Pending"],
        datasets: [
          {
            data: [70, 20, 10],
            backgroundColor: [
              theme.palette.success.main,
              theme.palette.warning.main,
              theme.palette.error.main,
            ],
            borderWidth: 0,
            cutout: '70%',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              usePointStyle: true,
              color: theme.palette.text.primary,
              font: {
                family: theme.typography.fontFamily,
                size: 12,
              },
              padding: 20,
            },
          },
        },
      },
    });

    return () => {
      if (clientChartInstanceRef.current) {
        clientChartInstanceRef.current.destroy();
      }
      if (revenueChartInstanceRef.current) {
        revenueChartInstanceRef.current.destroy();
      }
      if (taskChartInstanceRef.current) {
        taskChartInstanceRef.current.destroy();
      }
    };
  }, [theme]);

  const MetricCard = ({ icon, title, value, change, color, trend }) => (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        overflow: "visible",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 56,
              height: 56,
              border: `2px solid ${alpha(color, 0.2)}`,
            }}
          >
            {icon}
          </Avatar>
          {change && (
            <Chip
              icon={trend === "up" ? <GrowthIcon /> : null}
              label={change}
              size="small"
              sx={{
                bgcolor: trend === "up" ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                color: trend === "up" ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 600,
                border: "none",
              }}
            />
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const TaskProgressCard = ({ title, progress, color, subtitle, icon }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 40,
              height: 40,
              mr: 2,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {subtitle}
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: color }}
          >
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha(color, 0.1),
            "& .MuiLinearProgress-bar": {
              backgroundColor: color,
              borderRadius: 4,
            },
          }}
        />
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, subtitle, children }) => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: 350,
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ flex: 1, position: "relative" }}>
        {children}
      </Box>
    </Paper>
  );

  return (
    <Layout>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            CA Portal Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Welcome back! Here's what's happening with your practice today.
          </Typography>
        </Box>

        {/* Key Metrics Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<ClientIcon sx={{ fontSize: 28 }} />}
              title="Total Active Clients"
              value="256"
              change="+12%"
              color={theme.palette.info.main}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<RevenueIcon sx={{ fontSize: 28 }} />}
              title="Monthly Revenue"
              value="₹12,55,000"
              change="+8.2%"
              color={theme.palette.success.main}
              trend="up"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<TaskIcon sx={{ fontSize: 28 }} />}
              title="Active Tasks"
              value="42"
              change="-5%"
              color={theme.palette.warning.main}
              trend="down"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<WorkloadIcon sx={{ fontSize: 28 }} />}
              title="Team Capacity"
              value="85%"
              change="+3%"
              color={theme.palette.primary.main}
              trend="up"
            />
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Client Growth"
              subtitle="Monthly client acquisition trend"
            >
              <canvas ref={clientGrowthChartRef} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Quarterly Revenue"
              subtitle="Revenue performance by quarter"
            >
              <canvas ref={revenueChartRef} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Task Distribution"
              subtitle="Current task completion status"
            >
              <canvas ref={taskCompletionChartRef} />
            </ChartCard>
          </Grid>
        </Grid>

        {/* Task Progress Section */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Service Progress
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Track the progress of your key service areas
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TaskProgressCard
              title="Tax Filing"
              subtitle="Annual tax returns & compliance"
              progress={75}
              color={theme.palette.info.main}
              icon={<ReportIcon />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TaskProgressCard
              title="Audit Preparation"
              subtitle="Financial audit & documentation"
              progress={45}
              color={theme.palette.warning.main}
              icon={<ScheduleIcon />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TaskProgressCard
              title="Compliance Review"
              subtitle="Regulatory compliance checks"
              progress={90}
              color={theme.palette.success.main}
              icon={<CompleteIcon />}
            />
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default Cadashboard;
